import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, Loader } from 'lucide-react';
import { getCountriesFromFirebase } from '../services/FirebaseService';

// Selector de código de país (lada) reusable.
// Fuente de verdad: colección Firestore `res_country` (mismo que BookingForm),
// donde `id` es el res.country.id de Odoo = country_phone_id. México (MX) = 156.
//
// Emite el OBJETO país completo en onChange ({ id, code, name, phone_code }) para
// que el padre envíe el `id` correcto al backend y no un 0/null.
//
// Props:
//   value     — código ISO seleccionado (ej. 'MX')
//   onChange  — (country) => void
//   disabled  — bool
//   error     — bool (borde rojo)

const MX_FALLBACK = { id: 156, code: 'MX', name: 'México', phone_code: '+52' };

const CountryCodeSelect = ({ value, onChange, disabled = false, error = false }) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Cargar países una vez y sincronizar el país real (con su id) hacia el padre.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const list = await getCountriesFromFirebase();
      if (!active) return;
      setCountries(list);
      setLoading(false);
      const match = (value && list.find((c) => c.code === value)) || list.find((c) => c.code === 'MX');
      onChange?.(match || MX_FALLBACK);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    const onClickOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const selected =
    countries.find((c) => c.code === value) ||
    (value === MX_FALLBACK.code ? MX_FALLBACK : countries[0]) ||
    MX_FALLBACK;

  const term = search.trim().toLowerCase();
  const filtered = !term
    ? countries
    : countries.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.code.toLowerCase().includes(term) ||
          (c.phone_code || '').includes(search.trim())
      );

  const select = (country) => {
    onChange?.(country);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between gap-1 w-[88px] px-3 py-2.5 rounded-input bg-warm-canvas transition-colors focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-60 ${
          error ? 'ring-2 ring-red-400' : ''
        }`}
      >
        {loading ? (
          <Loader className="animate-spin text-daisies w-4 h-4 mx-auto" />
        ) : (
          <>
            <span className="font-bold text-daisies text-sm tabular-nums">{selected.phone_code}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-card shadow-elevated z-[90] overflow-hidden">
          <div className="p-2 border-b border-brand-100 bg-warm-canvas">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-secondary" />
              <input
                autoFocus
                type="text"
                placeholder="Buscar país…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-sm rounded-input bg-white focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-56 divide-y divide-brand-100">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-text-secondary text-sm">Sin resultados</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id || c.code}
                  type="button"
                  onClick={() => select(c)}
                  className={`w-full px-3 py-2.5 text-left hover:bg-daisies-50 transition-colors flex items-center justify-between text-sm ${
                    value === c.code ? 'bg-daisies-50' : ''
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-text-primary truncate">{c.name}</span>
                    <span className="text-xs text-text-secondary flex-shrink-0">({c.code})</span>
                  </span>
                  <span className="font-semibold text-daisies flex-shrink-0">{c.phone_code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
